import { FIXED_FEE_TIERS, getUniqueNetworks, getUniqueProtocols } from "../../data";
import { SUPPORTED_POOL_NETWORKS } from "../../supported-networks";

export const OTHER_FEE_TIER_FILTER_ID = "other";

export const FILTER_TOOLTIPS = {
	protocol: "Which version of Uniswap the liquidity pool is on.",
	network: `Which blockchain/network the liquidity pool is on. Unilytics supports ${SUPPORTED_POOL_NETWORKS.length} networks that account for 90%+ of Uniswap trade volume.`,
	tvl: "The size of the liquidity pool in USD. Useful for filtering out small pools with only a few liquidity providers.",
	keyword: "Search by token, token pair, or pool address.",
	"1dVolume":
		"How much trade volume the pool has seen over the past 24 hours. Useful for filtering out pools with little trading activity.",
	volTvl:
		"24h volume ÷ TVL. 1.00 means the pool traded its entire TVL in 24 hours. At the same fee tier, fee APR ≈ Vol/TVL × fee × 365 — so this is crowding/efficiency, not a second return number.",
	marketCap:
		"Limit your search to tokens that are in the Top 50-200 by market cap. Useful for filtering out small cap altcoins.",
	feeTier:
		"The percentage fee that Uniswap charges for trades in a pool. e.g. a $10,000 trade in a 0.30% fee tier pool = $30 paid to liquidity providers.",
} as const;

export const HEADERS = [
	{
		id: "favorite",
		label: "",
	},
	{
		id: "name",
		label: "Pool",
		isRowHeader: true,
	},
	{
		id: "token1",
		label: "Token 1",
	},
	{
		id: "token2",
		label: "Token 2",
	},
	{
		id: "uniswap",
		label: "Ver.",
	},
	{
		id: "network",
		label: "Network",
	},
	{
		id: "feeTier",
		label: "Fee Tier",
	},
	{
		id: "tvl",
		label: "TVL",
		allowsSorting: true,
	},
	{
		id: "apr1d",
		label: "APR (1D)",
		allowsSorting: true,
	},
	{
		id: "volTvl",
		label: "Vol/TVL (1D)",
		allowsSorting: true,
	},
	{
		id: "apr7d",
		label: "APR (7D)",
		allowsSorting: true,
	},
	{
		id: "apr30d",
		label: "APR (30D)",
		allowsSorting: true,
	},
	{
		id: "actions",
		label: "Analyze",
		allowsSorting: false,
	},
];

export const getSelectFilters = () => {
	const networks = getUniqueNetworks();
	const protocols = getUniqueProtocols();

	return {
		protocol: {
			label: "Protocol",
			tooltip: FILTER_TOOLTIPS.protocol,
			items: [
				{ id: "all", label: "All Protocols" },
				...protocols.map((p) => ({ id: p, label: `Uniswap ${p}` })),
			],
		},
		network: {
			label: "Network",
			tooltip: FILTER_TOOLTIPS.network,
			items: [{ id: "all", label: "All Networks" }, ...networks.map((n) => ({ id: n, label: n }))],
		},
		feeTier: {
			label: "Fee Tier",
			tooltip: FILTER_TOOLTIPS.feeTier,
			items: [
				{ id: "all", label: "All Fee Tiers" },
				...FIXED_FEE_TIERS.map((feeTier) => ({
					id: feeTier,
					label: feeTier,
				})),
				{ id: OTHER_FEE_TIER_FILTER_ID, label: "Other Fee Tiers" },
			],
		},
		tvl: {
			label: "TVL",
			tooltip: FILTER_TOOLTIPS.tvl,
			items: [
				{ id: "all", label: "Any TVL" },
				{ id: "10k", label: "> $10K TVL" },
				{ id: "100k", label: "> $100K TVL" },
				{ id: "1m", label: "> $1M TVL" },
			],
		},
		"1dVolume": {
			label: "1D Volume",
			tooltip: FILTER_TOOLTIPS["1dVolume"],
			items: [
				{ id: "all", label: "Any 1D Volume" },
				{ id: "1k", label: "> $1,000" },
				{ id: "10k", label: "> $10,000" },
				{ id: "100k", label: "> $100,000" },
			],
		},
		volTvl: {
			label: "Vol/TVL",
			tooltip: FILTER_TOOLTIPS.volTvl,
			items: [
				{ id: "all", label: "Any Vol/TVL" },
				{ id: "0.05", label: "> 0.05" },
				{ id: "0.2", label: "> 0.20" },
				{ id: "0.5", label: "> 0.50" },
				{ id: "1", label: "> 1.00" },
			],
		},
		marketCap: {
			label: "Market Cap",
			tooltip: FILTER_TOOLTIPS.marketCap,
			items: [
				{ id: "all", label: "Any Market Cap" },
				{ id: "top_50", label: "Top 50" },
				{ id: "top_100", label: "Top 100" },
				{ id: "top_200", label: "Top 200" },
			],
		},
	};
};

export const SELECT_FILTERS = getSelectFilters();

export const INPUT_FILTERS = {
	keyword: {
		label: "Keyword",
		placeholder: "Token, pair, or 0x…",
		tooltip: FILTER_TOOLTIPS.keyword,
	},
};
